<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12" md="6">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">Send a tracked email</v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="sendTrackedMail">
                            <v-text-field v-model="mailForm.to" label="Recipient (email)" required
                                outlined></v-text-field>
                            <v-text-field v-model="mailForm.subject" label="Subject" outlined></v-text-field>
                            <v-textarea v-model="mailForm.body"
                                label="Email body (you can use HTML and links with href)" rows="7"
                                outlined></v-textarea>
                            <v-btn :disabled="sending" type="submit" color="primary" class="mt-4">
                                {{ sending ? "Sending..." : "Send email" }}
                            </v-btn>
                            <v-alert v-if="mailStatus" :type="mailStatus.success ? 'success' : 'error'" class="mt-4">
                                {{ mailStatus.msg }}
                            </v-alert>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">🔗 Tracked link generator</v-card-title>
                    <v-card-text>
                        <v-text-field v-model="linkGen.email" label="Recipient (email)" outlined></v-text-field>
                        <v-text-field v-model="linkGen.url" label="Destination link" placeholder="https://..."
                            outlined></v-text-field>
                        <v-text-field v-model="linkGen.text" label="Displayed text" placeholder="E.g.: Click here"
                            outlined></v-text-field>
                        <v-alert v-if="linkGen.email && linkGen.url && linkGen.text" type="info" class="mt-4">
                            <div>
                                <label>HTML Link:</label>
                                <div
                                    style="background:#fff;color:black;border:1px solid #bcdff7;border-radius:7px;padding:10px;word-break:break-all;font-size:15px;">
                                    {{ generatedHtmlLink }}
                                </div>
                                <v-btn @click="copyGeneratedLink" color="primary" class="mt-2">
                                    📋 Copy HTML Link
                                </v-btn>
                            </div>
                            <div class="mt-4">
                                <label>Tracked URL:</label>
                                <div
                                    style="background:#fff;color:black;border:1px solid #bcdff7;border-radius:7px;padding:10px;word-break:break-all;font-size:15px;">
                                    {{ trackedUrl }}
                                </div>
                                <v-btn @click="copyTrackedUrl" color="primary" class="mt-2">
                                    📋 Copy Tracked URL
                                </v-btn>
                            </div>
                            <span v-if="copied" class="ml-2 text-success font-weight-bold">Copied!</span>
                        </v-alert>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">Filter by recipient email</v-card-title>
                    <v-card-text>
                        <v-text-field v-model="searchEmail" label="Search by recipient email" outlined></v-text-field>
                        <v-btn @click="resetFilter" color="primary" class="mt-2">
                            Reset
                        </v-btn>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">Synthetic statistics</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="6" md="3">
                                <v-card class="pa-4 text-center" outlined>
                                    <h3>{{ totalClicks }}</h3>
                                    <div>Total clicks</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card class="pa-4 text-center" outlined>
                                    <h3>{{ uniqueEmails }}</h3>
                                    <div>Unique recipients</div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">Click grouping by email and link</v-card-title>
                    <v-card-text>
                        <v-simple-table>
                            <thead>
                                <tr>
                                    <th>Recipient email</th>
                                    <th>Tracked link</th>
                                    <th>Click count</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in groupedClicks" :key="row.email + row.url">
                                    <td>{{ row.email }}</td>
                                    <td><a :href="row.url" target="_blank">{{ row.url }}</a></td>
                                    <td>{{ row.count }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="pa-4" outlined>
                    <v-card-title class="headline">Click details</v-card-title>
                    <v-card-text>
                        <v-simple-table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Recipient (Email)</th>
                                    <th>Link type</th>
                                    <th>URL</th>
                                    <th>Date/Time</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in filteredClicks" :key="idx">
                                    <td>{{ idx + 1 }}</td>
                                    <td>{{ row.email || '-' }}</td>
                                    <td>{{ row.type }}</td>
                                    <td><a :href="row.url" target="_blank">Link</a></td>
                                    <td>{{ formatDate(row.timestamp) }}</td>
                                    <td>{{ row.ip }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
    name: "EmailDashboard",
    data() {
        return {
            clicks: [],
            clicksByUser: {},
            totalClicks: 0,
            uniqueEmails: 0,
            chartInstance: null,
            mailForm: {
                to: '',
                subject: '',
                body: '',
            },
            sending: false,
            mailStatus: null,
            searchEmail: '',
            copied: false,
            linkGen: {
                email: '',
                url: '',
                text: '',
            }
        };
    },
    computed: {
        // ...altri computed...
        generatedHtmlLink() {
            if (!this.linkGen.email || !this.linkGen.url || !this.linkGen.text) return '';
            return `<a href="${this.buildTrackedLink(this.linkGen.email, this.linkGen.url)}">${this.linkGen.text}</a>`;
        },

        filteredClicks() {
            if (!this.searchEmail) return this.clicks;
            const term = this.searchEmail.toLowerCase();
            return this.clicks.filter(
                c => c.email && c.email.toLowerCase().includes(term)
            );
        },
        groupedClicks() {
            // Raggruppa per email+url e conta i click
            const groups = {};
            this.filteredClicks.forEach(row => {
                const key = `${row.email}___${row.url}`;
                if (!groups[key]) {
                    groups[key] = { email: row.email, url: row.url, count: 1 };
                } else {
                    groups[key].count++;
                }
            });
            return Object.values(groups);
        },
        trackedUrl() {
            if (!this.linkGen.email || !this.linkGen.url) return '';
            return this.buildTrackedLink(this.linkGen.email, this.linkGen.url);
        }
    },
    methods: {
        copyTrackedUrl() {
            navigator.clipboard.writeText(this.trackedUrl)
                .then(() => {
                    this.copied = true;
                    setTimeout(() => (this.copied = false), 1300);
                });
        },
        buildTrackedLink(email, url) {
            // Usa la stessa logica che hai in backend!
            // Puoi cambiare process.env.VUE_APP_TRACK_BASE_URL con il tuo valore effettivo.
            let recipientId;
            try {
                // Browser: usa subtle (ma non serve se basta l'email)
                recipientId = this.md5(email);
            } catch {
                // fallback per Node/electron
                recipientId = email;
            }
            const base = process.env.VUE_APP_TRACK_BASE_URL || 'https://www.e-photo.site:5001';
            return `${base}/track-click?id=${encodeURIComponent(recipientId)}&email=${encodeURIComponent(email)}&url=${encodeURIComponent(url)}`;
        },
        // Funzione MD5 base64 (solo browser, se vuoi hashare, oppure metti semplicemente recipientId = email)
        md5(str) {
            // Semplificato: puoi usare l'email come recipientId se vuoi
            return str; // oppure implementa una funzione hash se vuoi
        },
        copyGeneratedLink() {
            navigator.clipboard.writeText(this.generatedHtmlLink)
                .then(() => {
                    this.copied = true;
                    setTimeout(() => (this.copied = false), 1300);
                });
        },
        async fetchClicks() {
            const res = await fetch(process.env.VUE_APP_SERVER3 + '/api/clicks');
            this.clicks = await res.json();
            this.processStats();
            this.$nextTick(this.drawChart); // wait DOM update
        },
        processStats() {
            // Aggregate per user (mail)
            this.clicksByUser = this.clicks.reduce((acc, row) => {
                if (row.email) acc[row.email] = (acc[row.email] || 0) + 1;
                return acc;
            }, {});
            // Totali
            this.totalClicks = this.clicks.length;
            this.uniqueEmails = Object.keys(this.clicksByUser).length;
        },

        drawChart() {
            const canvas = this.$refs.barChart;
            if (!canvas || !canvas.getContext) return;
            const ctx = canvas.getContext('2d');
            if (this.chartInstance) this.chartInstance.destroy();

            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: Object.keys(this.clicksByUser),
                    datasets: [{
                        label: 'Click per destinatario (mail)',
                        data: Object.values(this.clicksByUser),
                        borderWidth: 2,
                    }]
                },
                options: {
                    plugins: { legend: { display: false } },
                    responsive: true,
                    scales: {
                        x: { beginAtZero: true, type: 'category' },
                        y: { beginAtZero: true, type: 'linear', ticks: { stepSize: 1 } }
                    }
                }
            });
        },

        async sendTrackedMail() {
            this.sending = true;
            this.mailStatus = null;
            try {
                const res = await fetch(process.env.VUE_APP_SERVER3 + '/send-mail-tracked', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.mailForm)
                });
                const data = await res.json();
                if (res.ok) {
                    this.mailStatus = { success: true, msg: data.message || "Mail inviata!" };
                    this.mailForm.body = '';
                } else {
                    this.mailStatus = { success: false, msg: data.error || "Errore invio mail" };
                }
            } catch (e) {
                this.mailStatus = { success: false, msg: "Errore di rete: " + e };
            }
            this.sending = false;
        },

        formatDate(dt) {
            if (!dt) return '';
            const d = new Date(dt);
            return d.toLocaleString();
        },
        resetFilter() {
            this.searchEmail = '';
        }
    },
    mounted() {
        this.fetchClicks();
    },
    watch: {
        clicks() {
            this.processStats();
            this.$nextTick(this.drawChart);
        }
    }
};
</script>

<style scoped>
.stat-box {
    background: #f1f6fb;
    border-radius: 12px;
    box-shadow: 0 2px 6px #c9e3fa77;
    padding: 24px 32px;
    text-align: center;
    min-width: 180px;
    flex: 1 1 180px;
}

.table-scroll {
    overflow-x: auto;
    margin-top: 10px;
}

table {
    border-collapse: collapse;
    width: 100%;
    min-width: 800px;
    background: #fff;
}

th,
td {
    padding: 10px 12px;
    border-bottom: 1px solid #e3e3e3;
    text-align: left;
}

th {
    background: #f7fafc;
    font-weight: 600;
    color: #1769aa;
}

tbody tr:hover {
    background: #f1f6fb;
}
</style>
